import { FormattedMessage, useIntl } from "react-intl";
import { Accordion } from "react-bootstrap";
import { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import CardNew from "components/molecules/Card/CardNew";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import Badge from "components/atoms/Badge/Badge";
import usePermissions from "hooks/usePermissions";
import errorToast from "helpers/errorToast";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Pagination from "components/v2/Pagination/Pagination";

interface IRelease {
  id: number;
  title: string;
  date: string;
  features: IChange[];
  improvements: IChange[];
  bugs: IChange[];
  video_urls: IVideo[];
}

interface IChange {
  id: number;
  title: string;
  description: string;
  company_slug: string;
}

interface IVideo {
  id: number;
  vimeo_id: string;
  company_slug: string;
}

const PAGE_SIZE = 25;

function ReleaseNotes() {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { company_name } = JSON.parse(permissions || "{}");

  const [activeAccordion, setActiveAccordion] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);

  /**
   * Filters release notes to remove any changes that are specific to a company that is not the current user's company
   * @param releaseNotes - list of release notes
   * @returns a filtered list of release notes
   */
  const filterReleaseNotes = (releaseNotesToFilter?: IRelease[]) =>
    releaseNotesToFilter?.map((release: IRelease) => ({
      ...release,
      features: release.features.filter(
        (feature: IChange) =>
          feature.company_slug === null ||
          feature.company_slug === company_name ||
          typeof feature.company_slug === "undefined",
      ),
      bugs: release.bugs.filter(
        (bug: IChange) =>
          bug.company_slug === null || bug.company_slug === company_name || typeof bug.company_slug === "undefined",
      ),
      improvements: release.improvements.filter(
        (improvement: IChange) =>
          improvement.company_slug === null ||
          improvement.company_slug === company_name ||
          typeof improvement.company_slug === "undefined",
      ),
      video_urls: release.video_urls.filter(
        (video: IVideo) =>
          video.company_slug === null || video.company_slug === company_name || typeof video.company_slug === "undefined",
      ),
    }));

  const fetchReleaseNotes = async (page: number) => {
    const response = await axios.get("https://cargobasecms.herokuapp.com/api/release-notes", {
      params: {
        populate: "*",
        sort: "id:DESC",
        "pagination[page]": page,
        "pagination[pageSize]": PAGE_SIZE,
      },
    });
    return response.data;
  };

  const { data: releaseNotesData, isLoading, error: releaseNotesError } = useQuery({
    queryKey: ["releaseNotes", page],
    queryFn: () => fetchReleaseNotes(page),
  });

  useEffect(() => {
    if (releaseNotesError) {
      errorToast(releaseNotesError);
    }
  }, [releaseNotesError]);
  const releaseNotes = filterReleaseNotes(
    releaseNotesData?.data?.map((release: any) => ({
      id: release.id,
      ...release.attributes,
    })),
  );

  useEffect(() => {
    if (releaseNotes && releaseNotes.length > 0) {
      setActiveAccordion(releaseNotes[0].id.toString());
    }
  }, [releaseNotesData]);

  /**
   * Renders the list of changes including the section badge
   * @param changes - list of changes
   * @param type - type of change
   * @returns a JSX element, a div containing the change type badge and the list of changes
   */
  const renderChanges = (changes: any[], type: "Features" | "Improvements" | "Bugs") => {
    if (changes.length === 0) return null;

    // Set badge type
    let badgeElement: JSX.Element;
    switch (type) {
      case "Features":
        badgeElement = (
          <Badge type="primary" label={intl.formatMessage({ id: "releaseNotes.features", defaultMessage: "Features" })} />
        );
        break;
      case "Improvements":
        badgeElement = (
          <Badge
            type="tertiary"
            label={intl.formatMessage({ id: "releaseNotes.improvements", defaultMessage: "Improvements" })}
          />
        );
        break;
      case "Bugs":
        badgeElement = (
          <Badge type="danger" label={intl.formatMessage({ id: "releaseNotes.fixes", defaultMessage: "Fixes" })} />
        );
        break;
      default:
        badgeElement = <Badge type="warning" label={type} />;
        break;
    }

    // Render change list
    return (
      <div className="mt-2 mb-4">
        <div className="d-flex">{badgeElement}</div>
        <div className="mb-2">
          {changes.map((change: any) => (
            <ul>
              <li className="font-b2 m-2">&#8226; {change.title}</li>
              {change.description && (
                <ul>
                  <li className="font-b2 ml-4">- {change.description}</li>
                </ul>
              )}
            </ul>
          ))}
        </div>
      </div>
    );
  };

  return (
    <CardNew>
      <h3 className="mb-5">
        <FormattedMessage id="releaseNotes.releaseNotes" defaultMessage="Release notes" />
      </h3>
      {isLoading && <LoadingSkeleton rows={3} />}
      {!isLoading &&
        releaseNotes?.map((release) => (
          <Accordion
            key={release.id}
            onSelect={(eventKey) => setActiveAccordion(eventKey as string)}
            activeKey={activeAccordion}
            className="mb-3"
          >
            <Accordion.Toggle as="div" eventKey={release.id.toString()} className="cursor-pointer">
              <div className="d-flex align-items-center justify-content-between">
                <h5>{release.title}</h5>
                <div className="d-flex align-items-center">
                  <p className="font-b1">{formatDate(release.date, DateTimeFormat.dateFormat)}</p>
                  <SortIcon
                    className="ml-3"
                    fill="#000"
                    width={8}
                    height={7}
                    transform={`rotate(${activeAccordion === release.id.toString() ? 180 : 0})`}
                  />
                </div>
              </div>
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={release.id.toString()} className="pt-2">
              <div>
                {renderChanges(release.features, "Features")}
                {renderChanges(release.improvements, "Improvements")}
                {renderChanges(release.bugs, "Bugs")}
                {release.video_urls.length > 0 && (
                  <>
                    <h6 className="mb-3">
                      <FormattedMessage id="releaseNotes.releaseVideos" defaultMessage="Release videos" />
                    </h6>
                    {release.video_urls.map((video: any) => (
                      <div className="mb-2">
                        <ReactPlayer key={video.id} url={`https://vimeo.com/${video.vimeo_id}`} controls />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </Accordion.Collapse>
          </Accordion>
        ))}
      <div className="d-flex justify-content-center mt-lg">
        <Pagination
          totalPages={Math.ceil(releaseNotesData?.meta?.pagination?.total / PAGE_SIZE)}
          currentPage={page}
          pageClicked={(page) => setPage(page)}
        />
      </div>
    </CardNew>
  );
}

export default ReleaseNotes;
